import React, { useRef } from "react";
import { Box, IconButton, useTheme } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";

interface ClientPOFilePickerProps {
  file: File | null;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  onSelect: (file: File) => void;
  onClear: () => void;
}

/** Compact PDF picker that looks like a text field: click to browse, X to clear. */
const ClientPOFilePicker: React.FC<ClientPOFilePickerProps> = ({
  file,
  placeholder = "Choose PO PDF",
  disabled = false,
  error = false,
  onSelect,
  onClear,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    if (picked) onSelect(picked);
    event.target.value = "";
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={handleChange}
        disabled={disabled}
      />
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(event) => {
          if (!disabled && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: 40,
          px: 1.25,
          borderRadius: "4px",
          border: `1px ${file ? "solid" : "dashed"} ${
            error ? theme.palette.error.main : colors.borderSoft
          }`,
          bgcolor: disabled
            ? theme.palette.action.disabledBackground
            : "transparent",
          cursor: disabled ? "default" : "pointer",
          transition: "border-color 0.15s ease",
          "&:hover": disabled ? {} : { borderColor: colors.accentPurple },
          minWidth: 0,
        }}
      >
        <UploadFileOutlinedIcon
          sx={{ fontSize: 18, color: file ? colors.accent : colors.subtitle }}
        />
        <CustomTypography
          variant="body2"
          color={file ? colors.title : colors.subtitle}
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {file
            ? `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`
            : placeholder}
        </CustomTypography>
        {file && !disabled && (
          <IconButton
            size="small"
            title="Remove file"
            aria-label="Remove file"
            onClick={(event) => {
              event.stopPropagation();
              onClear();
            }}
            sx={{ p: 0.25 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </>
  );
};

export default ClientPOFilePicker;
